import { Pic } from "@/components/site/Pic";

import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Contact } from "@/components/site/Contact";
import { MaskLines, Reveal, SectionLabel } from "@/components/site/Motion";
import { SheetDownloads } from "@/components/site/SheetDownloads";
import { LamellareFaq } from "@/components/site/LamellareFaq";
import { SectionCalc } from "@/components/site/SectionCalc";
import { DataTable } from "@/components/site/DataTable";
import { scrollToId } from "@/lib/scroll";
import { useT, useLang } from "@/i18n";
import { usePageMeta } from "@/lib/pageMeta";
import DATA from "@/data/lamellare.json";

const STATS = [["44 m", "lam.s0", "lunghezza massima"], ["30 × 280 cm", "lam.s1", "sezione massima"], ["GL20 – GL32", "lam.s2", "classi di resistenza"], ["EN 14080", "lam.s3", "marcatura CE"]];

const Rows = ({ rows, id }) => (
  <dl className="divide-y divide-[#121110]/15 border-y border-[#121110]/15" data-testid={`lam-rows-${id}`}>
    {rows.map(([k, v]) => (
      <div key={k} className="grid gap-1 py-4 sm:grid-cols-5 sm:gap-6">
        <dt className="font-mono text-xs uppercase tracking-[0.15em] text-[#6E685E] sm:col-span-2">{k}</dt>
        <dd className="text-base sm:col-span-3">{v}</dd>
      </div>
    ))}
  </dl>
);

const Block = ({ s, id, aside, children }) => (
  <Reveal className="grid gap-8 lg:grid-cols-12" data-testid={`lam-block-${id}`}>
    <div className="lg:col-span-4">
      <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{s.title}</h2>
      <p className="mt-2 text-[#3D3833]">{s.sub}</p>
      {aside}
    </div>
    <div className="min-w-0 lg:col-span-8">
      {children}
      <p className="mt-4 text-sm text-[#5A544C]">{s.note}</p>
    </div>
  </Reveal>
);

const ClassesTable = ({ s }) => {
  const c = DATA.classes;
  return (
    <div className="overflow-x-auto" data-testid="lam-classes-table">
      <table className="w-full min-w-[640px] border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-[#121110]/25">
            <th className="py-3 pr-4 font-mono text-xs uppercase tracking-[0.15em]">GL</th>
            {c.cols.map((col, i) => (
              <th key={col} className="py-3 pr-4 align-bottom font-normal">
                <span className="block text-xs text-[#5A544C]">{s.labels[i]}</span>
                <span className="font-mono text-xs">{col} [{c.units[i]}]</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {c.rows.map(([cls, ...vals]) => (
            <tr key={cls} className="border-b border-[#121110]/10">
              <td className="py-3 pr-4 font-display font-bold text-[#9A5326]">{cls}</td>
              {vals.map((v, i) => <td key={i} className="py-3 pr-4 tabular-nums">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CurvedFigure = ({ t }) => (
  <figure className="mt-8" data-testid="lam-curved-figure">
    <div className="aspect-[3/2] overflow-hidden bg-[#E4E2DE]">
      <Pic src="/img/emma/p-travi-curve.jpg" alt={t("lamp.curvedAlt", "Trave curva in legno lamellare EMMA INDUSTRIE su supporti in acciaio")} loading="lazy" className="h-full w-full object-cover" data-testid="lam-curved-img" />
    </div>
    <figcaption className="mt-3 text-sm text-[#5A544C]" data-testid="lam-curved-caption">{t("lamp.curvedCap", "Trave curva in lamellare: archi, travi a S e a doppia S prodotti su misura nel nostro stabilimento.")}</figcaption>
  </figure>
);

const Hero = ({ t }) => (
  <section className="px-5 pb-20 pt-10 sm:px-8" data-testid="lam-hero">
    <div className="mx-auto max-w-[1500px]">
      <Link to="/" className="tap inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#D6D0C4] transition-colors duration-300 hover:text-[#D98A52]" data-testid="lam-page-back">
        <ArrowLeft className="h-4 w-4" /> {t("beachPage.back", "Torna alla home")}
      </Link>
      <div className="mt-14"><SectionLabel index="EI" dark>{t("lam.label", "Legno lamellare strutturale")}</SectionLabel></div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
        <MaskLines as="h1" onLoad className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-6xl"
          lines={[t("lamp.h1a", "Legno lamellare"), <em key="e" className="font-serif font-normal text-[#D98A52]">{t("lamp.h1em", "strutturale.")}</em>]} />
        <Reveal delay={0.2} className="max-w-md lg:col-span-5">
          <p className="text-base leading-relaxed text-[#D6D0C4] sm:text-lg">{t("lamp.intro", "Scheda tecnica, classi di resistenza e dimensioni del legno lamellare EMMA INDUSTRIE, con le schede di lavoro in PDF per impostare il tuo progetto.")}</p>
          <button onClick={() => scrollToId("#schede")} className="btn-amber mt-8" data-testid="lam-hero-cta">{t("lamp.cta", "Scarica le schede in PDF")} <ArrowUpRight className="h-4 w-4" /></button>
        </Reveal>
      </div>
      <p className="mt-16 max-w-3xl border-l-2 border-[#D98A52] pl-6 font-serif text-2xl italic leading-snug text-[#F3EFE6] sm:text-3xl" data-testid="lam-claim">
        {t("lam.claim", "Ogni lamella è selezionata e prodotta per resistere generazioni.")}
      </p>
      <div className="mt-16 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4" data-testid="lam-stats">
        {STATS.map(([v, k, l]) => (
          <div key={k} className="bg-[#1A1714] p-6">
            <p className="font-display text-2xl font-bold text-[#D98A52] sm:text-3xl">{v}</p>
            <p className="mt-1 text-sm text-[#D6D0C4]">{t(k, l)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function LegnoLamellare() {
  const t = useT();
  const { lang } = useLang();
  const d = DATA[lang] ?? DATA.it;
  usePageMeta({
    title: t("lamp.metaTitle", "Legno lamellare strutturale: scheda tecnica e classi di resistenza"),
    desc: t("lamp.metaDesc", "Legno lamellare EMMA INDUSTRIE secondo EN 14080: classi di resistenza da GL20 a GL32, travi fino a 44 m e sezioni fino a 30 × 280 cm. Scarica le schede tecniche in PDF."),
    path: "/legno-lamellare",
    image: "/og-lamellare.jpg",
    brand: "EMMA INDUSTRIE",
  });
  return (
    <div className="bg-[#1A1714] text-[#F3EFE6]" data-testid="lam-page">
      <Nav />
      <main className="pt-24">
        <Hero t={t} />
        <section className="space-y-24 bg-[#F7F5F0] px-5 py-24 text-[#121110] sm:px-8 lg:py-32" data-testid="lam-specs">
          <div className="mx-auto max-w-[1500px] space-y-24">
            <Block s={d.sheets.tecnica} id="tecnica"><Rows rows={d.sheets.tecnica.rows} id="tecnica" /></Block>
            <Block s={d.sheets.classi} id="classi"><ClassesTable s={d.sheets.classi} /></Block>
            <Block s={d.sheets.dimensioni} id="dimensioni" aside={<CurvedFigure t={t} />}><Rows rows={d.sheets.dimensioni.rows} id="dimensioni" /></Block>
            <Block s={d.sheets.xlam} id="xlam">
              <DataTable cols={d.sheets.xlam.cols} rows={DATA.xlam} id="xlam" />
              <p className="mt-3 text-sm text-[#5A544C]">{d.sheets.xlam.nominal}</p>
              <h3 className="mt-12 font-display text-xl font-bold">{d.sheets.xlam.propsTitle}</h3>
              <div className="mt-4"><DataTable cols={d.sheets.xlam.propsCols} rows={d.sheets.xlam.props} id="xlam-cl24" bold={3} /></div>
              <Link to="/pannelli-xlam" className="tap mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#9A5326] hover:underline" data-testid="lam-xlam-link">{t("xp.link", "Pagina dei pannelli X-LAM")} <ArrowUpRight className="h-4 w-4" /></Link>
            </Block>
          </div>
        </section>
        <SheetDownloads keys={["catalogo", "tecnica", "classi", "dimensioni", "xlam"]} />
        <SectionCalc />
        <LamellareFaq />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
